import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { ClienteService } from './cliente.service';
import { Cliente } from '../../domain/cliente.domain';

/**
 * Opción de cliente para uso en combos/dropdowns
 */
export interface ClienteOption {
  id: number | string;
  label: string;
  subLabel?: string;
  cliente: Cliente;
}

/**
 * Parámetros para búsqueda de clientes en lookup
 */
export interface ClienteLookupParams {
  tipoCliente: 'agencia' | 'anunciante' | 'productor';
  agenciaProductorId?: number | string | null;
  term?: string;
  page?: number;
  itemsPerPage?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ClienteLookupService {
  private clienteService = inject(ClienteService);
  
  /**
   * Cache en memoria: key -> Observable<ClienteOption[]>
   * Key formato: tipoCliente|agenciaProductorId|term|page|itemsPerPage
   */
  private cache = new Map<string, Observable<ClienteOption[]>>();

  /**
   * Obtiene clientes filtrados por tipo y otros parámetros, con cache
   */
  getClientesLookup(params: ClienteLookupParams): Observable<ClienteOption[]> {
    const {
      tipoCliente,
      agenciaProductorId = null,
      term = '',
      page = 1,
      itemsPerPage = 50
    } = params;

    const cacheKey = this.buildCacheKey(tipoCliente, agenciaProductorId, term, page, itemsPerPage);

    // Si ya existe en cache, retornar el observable cacheado
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Construir parámetros HTTP
    let httpParams = new HttpParams()
      .set('tipoCliente', tipoCliente)
      .set('size', itemsPerPage.toString())
      .set('page', page.toString())
      .set('_order[razonSocial]', 'asc');

    // Agregar filtro de agenciaProductor si se proporciona
    if (agenciaProductorId !== null && agenciaProductorId !== undefined && agenciaProductorId !== '') {
      httpParams = httpParams.set('agenciaProductor', `/api/clientes/${agenciaProductorId}`);
    }

    // Agregar término de búsqueda si existe
    if (term && term.trim()) {
      httpParams = httpParams.set('q', term.trim());
    }

    // Realizar la búsqueda
    const request$ = this.clienteService.searchClientes(httpParams).pipe(
      map((members: Cliente[]) => this.mapToOptions(members)),
      shareReplay(1), // Compartir el resultado entre múltiples suscriptores
      catchError((error) => {
        console.error('Error en ClienteLookupService:', error);
        return of([]); // Retornar array vacío en caso de error
      })
    );

    // Guardar en cache
    this.cache.set(cacheKey, request$);

    return request$;
  }

  /**
   * Obtiene un cliente por ID y lo agrega a las opciones si no está presente
   */
  getClienteById(id: number | string): Observable<ClienteOption | null> {
    return this.clienteService.getById(id).pipe(
      map((cliente: Cliente) => {
        if (!cliente) {
          return null;
        }
        return this.mapToOption(cliente);
      }),
      catchError((error) => {
        console.error('Error al obtener cliente por ID:', error);
        return of(null);
      })
    );
  }

  /**
   * Limpia el cache (útil cuando se crea/actualiza un cliente)
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Limpia el cache para un tipo específico
   */
  clearCacheByType(tipoCliente: 'agencia' | 'anunciante' | 'productor'): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${tipoCliente}|`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Mapea un array de Cliente a ClienteOption[]
   */
  private mapToOptions(clientes: Cliente[]): ClienteOption[] {
    return clientes.map(cliente => this.mapToOption(cliente));
  }

  /**
   * Mapea un Cliente a ClienteOption
   */
  private mapToOption(cliente: Cliente): ClienteOption {
    const id = this.extractId(cliente);
    const label = this.buildLabel(cliente);
    const subLabel = this.buildSubLabel(cliente);

    return {
      id,
      label,
      subLabel,
      cliente
    };
  }

  /**
   * Construye el label principal (razonSocial, nombreFantasia, o nombre+apellido)
   */
  private buildLabel(cliente: Cliente): string {
    if (cliente.razonSocial) {
      return cliente.razonSocial;
    }
    if (cliente.nombreFantasia) {
      return cliente.nombreFantasia;
    }
    if (cliente.nombre && cliente.apellido) {
      return `${cliente.nombre} ${cliente.apellido}`;
    }
    if (cliente.nombre) {
      return cliente.nombre;
    }
    return `Cliente #${this.extractId(cliente) || 'N/A'}`;
  }

  /**
   * Construye el sublabel (cuit, email, etc.)
   */
  private buildSubLabel(cliente: Cliente): string | undefined {
    if (cliente.cuit) {
      return cliente.cuit;
    }
    if (cliente.email) {
      return cliente.email;
    }
    return undefined;
  }

  /**
   * Extrae el ID de un cliente (puede ser string o number)
   */
  private extractId(cliente: Cliente): number | string {
    if (cliente.id) {
      return cliente.id;
    }
    // Intentar obtener el ID desde @id si existe (formato Hydra)
    const clienteAny = cliente as Record<string, unknown>;
    if (typeof clienteAny['@id'] === 'string') {
      const match = (clienteAny['@id'] as string).match(/\/(\d+)$/);
      if (match) {
        return Number(match[1]);
      }
    }
    return 'N/A';
  }

  /**
   * Construye la key del cache
   */
  private buildCacheKey(
    tipoCliente: string,
    agenciaProductorId: number | string | null | undefined,
    term: string,
    page: number,
    itemsPerPage: number
  ): string {
    const agenciaId = agenciaProductorId ?? 'null';
    const searchTerm = (term || '').trim().toLowerCase();
    return `${tipoCliente}|${agenciaId}|${searchTerm}|${page}|${itemsPerPage}`;
  }
}
